import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Grid,
	ListSubheader,
	MenuItem,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ResponsiveContainer,
	LineChart,
	CartesianGrid,
	Line,
	XAxis,
	YAxis,
	Tooltip,
} from "recharts";
import dayjs from "dayjs";
import {
	fetchDealerInsightsHistory,
	fetchDealerInsightsLatest,
} from "../../api/dealers";

const numberFormatter = (value, options = {}) => {
	if (value == null || Number.isNaN(value)) return "—";
	return new Intl.NumberFormat(undefined, options).format(value);
};

const formatPercent = (value, digits = 1) => {
	if (value == null || Number.isNaN(value)) return "—";
	return `${numberFormatter(value, {
		maximumFractionDigits: digits,
		minimumFractionDigits: Math.min(1, digits),
	})}%`;
};

const formatPeriod = (period) => {
	if (!period?.start || !period?.end) return "—";
	const start = dayjs(period.start);
	const end = dayjs(period.end);
	if (!start.isValid() || !end.isValid()) return "—";
	return `${start.format("DD MMM YYYY")} – ${end.format("DD MMM YYYY")}`;
};

const METRIC_PALETTE = [
	"#1abc9c",
	"#3498db",
	"#9b59b6",
	"#e67e22",
	"#e74c3c",
	"#34495e",
	"#16a085",
	"#2ecc71",
	"#8e44ad",
	"#f1c40f",
	"#d35400",
	"#7f8c8d",
];

const HISTORY_RANGE_OPTIONS = [
	{ key: "4", label: "Last 4 weeks", count: 4 },
	{ key: "8", label: "Last 8 weeks", count: 8 },
	{ key: "12", label: "Last 12 weeks", count: 12 },
	{ key: "all", label: "All weeks", count: Infinity },
];

const SINGLE_SECTIONS = [
	{
		key: "scores",
		title: "Scores",
		span: 12,
		metrics: [
			{
				label: "Overall Score",
				getValue: (week) => week?.score,
				formatter: (v) => formatPercent(v, 1),
			},
			{
				label: "Energy Score",
				getValue: (week) => week?.breakdown?.energy,
				formatter: (v) => formatPercent(v, 1),
			},
			{
				label: "Utilization Score",
				getValue: (week) => week?.breakdown?.utilization,
				formatter: (v) => formatPercent(v, 1),
			},
			{
				label: "Growth Score",
				getValue: (week) => week?.breakdown?.growth,
				formatter: (v) => formatPercent(v, 1),
			},
			{
				label: "Health Score",
				getValue: (week) => week?.breakdown?.health,
				formatter: (v) => formatPercent(v, 1),
			},
			{
				label: "Behavior Score",
				getValue: (week) => week?.breakdown?.behavior,
				formatter: (v) => formatPercent(v, 1),
			},
		],
	},
	{
		key: "utilization",
		title: "Utilization",
		span: 6,
		metrics: [
			{
				label: "Avg Distance",
				getValue: (week) => week?.metrics?.utilization?.avg_distance_km,
				formatter: (v) =>
					`${numberFormatter(v, { maximumFractionDigits: 1 })} km`,
			},
			{
				label: "Fleet Active Ratio",
				getValue: (week) =>
					typeof week?.metrics?.utilization?.fleet_active_ratio === "number"
						? week.metrics.utilization.fleet_active_ratio * 100
						: null,
				formatter: (v) => formatPercent(v, 1),
			},
		],
	},
	{
		key: "energy",
		title: "Energy",
		span: 6,
		metrics: [
			{
				label: "Avg km per kWh",
				getValue: (week) => week?.metrics?.energy?.avg_km_per_kwh,
				formatter: (v) =>
					`${numberFormatter(v, { maximumFractionDigits: 2 })} km/kWh`,
			},
		],
	},
	{
		key: "health",
		title: "Health",
		span: 6,
		metrics: [
			{
				label: "Avg Uptime",
				getValue: (week) => week?.metrics?.health?.avg_uptime_pct,
				formatter: (v) => formatPercent(v, 1),
			},
			{
				label: "Alerts / 1000 km",
				getValue: (week) => week?.metrics?.health?.avg_alerts_per_1000km,
				formatter: (v) =>
					numberFormatter(v, { maximumFractionDigits: 1 }),
			},
		],
	},
	{
		key: "data",
		title: "Data Quality",
		span: 6,
		metrics: [
			{
				label: "Data Completeness",
				getValue: (week) => week?.metrics?.data?.avg_completeness_pct,
				formatter: (v) => formatPercent(v, 1),
			},
		],
	},
	{
		key: "growth",
		title: "Growth",
		span: 6,
		metrics: [
			{
				label: "Growth Rate (4 weeks)",
				getValue: (week) =>
					typeof week?.metrics?.growth?.growth_rate_4w === "number"
						? week.metrics.growth.growth_rate_4w * 100
						: null,
				formatter: (v) => formatPercent(v, 1),
			},
		],
	},
];

const HISTORY_METRIC_DEFS = [
	{
		key: "score",
		label: "Overall Score",
		type: "percent",
		color: METRIC_PALETTE[0],
		getValue: (row) => row?.score,
		digits: 1,
	},
	{
		key: "avg_distance_km",
		label: "Avg Distance (km)",
		type: "number",
		unit: "km",
		color: METRIC_PALETTE[1],
		getValue: (row) => row?.metrics?.utilization?.avg_distance_km,
		digits: 1,
	},
	{
		key: "avg_km_per_kwh",
		label: "Avg km per kWh",
		type: "number",
		unit: "km/kWh",
		color: METRIC_PALETTE[2],
		getValue: (row) => row?.metrics?.energy?.avg_km_per_kwh,
		digits: 2,
	},
	{
		key: "avg_uptime_pct",
		label: "Avg Uptime",
		type: "percent",
		color: METRIC_PALETTE[3],
		getValue: (row) => row?.metrics?.health?.avg_uptime_pct,
		digits: 1,
	},
	{
		key: "avg_alerts_per_1000km",
		label: "Alerts / 1000 km",
		type: "number",
		color: METRIC_PALETTE[4],
		getValue: (row) => row?.metrics?.health?.avg_alerts_per_1000km,
		digits: 1,
	},
	{
		key: "avg_completeness_pct",
		label: "Data Completeness",
		type: "percent",
		color: METRIC_PALETTE[5],
		getValue: (row) => row?.metrics?.data?.avg_completeness_pct,
		digits: 1,
	},
	{
		key: "growth_rate_4w",
		label: "Growth Rate (4w)",
		type: "percent",
		color: METRIC_PALETTE[6],
		getValue: (row) =>
			typeof row?.metrics?.growth?.growth_rate_4w === "number"
				? row.metrics.growth.growth_rate_4w * 100
				: null,
		digits: 1,
	},
	{
		key: "energy_score",
		label: "Energy Score",
		type: "percent",
		color: METRIC_PALETTE[7],
		getValue: (row) => row?.breakdown?.energy,
		digits: 1,
	},
	{
		key: "utilization_score",
		label: "Utilization Score",
		type: "percent",
		color: METRIC_PALETTE[8],
		getValue: (row) => row?.breakdown?.utilization,
		digits: 1,
	},
	{
		key: "growth_score",
		label: "Growth Score",
		type: "percent",
		color: METRIC_PALETTE[9],
		getValue: (row) => row?.breakdown?.growth,
		digits: 1,
	},
	{
		key: "health_score",
		label: "Health Score",
		type: "percent",
		color: METRIC_PALETTE[10],
		getValue: (row) => row?.breakdown?.health,
		digits: 1,
	},
	{
		key: "behavior_score",
		label: "Behavior Score",
		type: "percent",
		color: METRIC_PALETTE[11],
		getValue: (row) => row?.breakdown?.behavior,
		digits: 1,
	},
];

const parseYearWeek = (value) => {
	if (!value) return null;
	const str = typeof value === "string" ? value : String(value);
	if (/^\d{6}$/.test(str)) {
		return { year: str.slice(0, 4), week: str.slice(4) };
	}
	const match = str.match(/^(\d{4})-?W?(\d{1,2})$/i);
	if (match) {
		const [, year, week] = match;
		return { year, week: week.padStart(2, "0") };
	}
	return null;
};

const shortWeekLabel = (value, index) => {
	const parsed = parseYearWeek(value);
	if (parsed) return `W${parsed.week}`;
	return value || `#${index + 1}`;
};

const fullWeekLabel = (value, index) => {
	const parsed = parseYearWeek(value);
	if (parsed) return `Week ${parsed.week} • ${parsed.year}`;
	return value ? `Week ${value}` : `Week ${index + 1}`;
};

const coerceNumber = (value) => {
	if (value == null) return null;
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

const formatHistoryValue = (value, def) => {
	if (value == null || Number.isNaN(value)) return "—";
	if (def.type === "percent") return formatPercent(value, def.digits ?? 1);
	const formatted = numberFormatter(value, {
		maximumFractionDigits: def.digits ?? 1,
	});
	return def.unit ? `${formatted} ${def.unit}` : formatted;
};

const formatHistoryTick = (value, def) => {
	if (value == null || Number.isNaN(value)) return "";
	if (def.type === "percent") {
		const digits = def.tickDigits ?? def.digits ?? 0;
		return formatPercent(value, digits);
	}
	const formatted = numberFormatter(value, {
		maximumFractionDigits: def.tickDigits ?? 0,
	});
	return def.unit ? `${formatted} ${def.unit}` : formatted;
};

const formatHistoryDelta = (latest, previous, def) => {
	if (latest == null || previous == null) return null;
	const delta = latest - previous;
	if (delta === 0) return "No change";
	const sign = delta > 0 ? "+" : "−";
	const abs = Math.abs(delta);
	if (def.type === "percent") {
		const formatted = numberFormatter(abs, {
			maximumFractionDigits: def.digits ?? 1,
			minimumFractionDigits: Math.min(1, def.digits ?? 1),
		});
		return `${sign}${formatted} pts`;
	}
	const formatted = numberFormatter(abs, {
		maximumFractionDigits: def.digits ?? 1,
	});
	return `${sign}${formatted}${def.unit ? ` ${def.unit}` : ""}`;
};

export default function DealerInsights({ dealerId, dealerName }) {
	const [latest, setLatest] = useState(null);
	const [history, setHistory] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [selectedWeek, setSelectedWeek] = useState(null);
	const [rangeKey, setRangeKey] = useState(HISTORY_RANGE_OPTIONS[0].key);
	const [selectedMetricKey, setSelectedMetricKey] = useState(
		HISTORY_METRIC_DEFS[0].key
	);

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	useEffect(() => {
		if (!dealerId) return;
		const controller = new AbortController();
		setLoading(true);
		setError(null);
		setLatest(null);
		setHistory([]);

		Promise.all([
			fetchDealerInsightsLatest(dealerId, controller.signal),
			fetchDealerInsightsHistory(dealerId, controller.signal),
		])
			.then(([latestResp, historyResp]) => {
				setLatest(latestResp);
				const ordered =
					Array.isArray(historyResp) && historyResp.length > 0
						? historyResp.sort((a, b) => {
								const tsA = dayjs(a.period?.start || a.computed_at).valueOf();
								const tsB = dayjs(b.period?.start || b.computed_at).valueOf();
								return tsB - tsA;
						  })
						: [];
				setHistory(ordered);
				if (ordered.length > 0) setSelectedWeek(String(ordered[0].year_week));
				else if (latestResp?.year_week) setSelectedWeek(String(latestResp.year_week));
			})
			.catch((err) => {
				if (controller.signal.aborted) return;
				setError(err?.message || "Unable to load dealer insights");
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});

		return () => controller.abort();
	}, [dealerId]);

	const weekEntries = useMemo(() => {
		const combined = history && history.length ? history.slice() : [];
		if (
			latest &&
			(latest.year_week == null ||
				!combined.some(
					(item) => String(item.year_week) === String(latest.year_week)
				))
		) {
			combined.unshift(latest);
		}
		return combined.map((entry, index) => ({
			...entry,
			week_id:
				entry?.year_week != null
					? String(entry.year_week)
					: `week-${index}`,
		}));
	}, [history, latest]);

	useEffect(() => {
		if (!weekEntries.length) return;
		if (
			!selectedWeek ||
			!weekEntries.some((item) => item.week_id === selectedWeek)
		) {
			setSelectedWeek(weekEntries[0].week_id);
		}
	}, [weekEntries, selectedWeek]);

	const selectedWeekEntry = useMemo(() => {
		if (!selectedWeek) return weekEntries[0] ?? null;
		return (
			weekEntries.find((item) => item.week_id === selectedWeek) ?? null
		);
	}, [weekEntries, selectedWeek]);

	const weekOptions = useMemo(() => {
		return weekEntries.map((item, index) => {
			const label = fullWeekLabel(item.year_week, index);
			return {
				id: item.week_id,
				label,
				period: formatPeriod(item.period),
			};
		});
	}, [weekEntries]);

	const selectedRange = useMemo(() => {
		const option =
			HISTORY_RANGE_OPTIONS.find((opt) => opt.key === rangeKey) ??
			HISTORY_RANGE_OPTIONS[0];
		return option;
	}, [rangeKey]);

	const filteredHistory = useMemo(() => {
		if (!history.length) return [];
		const count = selectedRange.count;
		const slice =
			count === Infinity ? history.slice() : history.slice(0, count);
		return slice
			.map((row, index) => {
				const tsSource = row.period?.start || row.computed_at;
				const ts = dayjs(tsSource).isValid()
					? dayjs(tsSource).valueOf()
					: index;
				return { ...row, timestamp: ts };
			})
			.sort((a, b) => a.timestamp - b.timestamp);
	}, [history, selectedRange]);

	const groupedHistoryCharts = useMemo(() => {
		return HISTORY_METRIC_DEFS.map((def, index) => {
			const data = filteredHistory.map((row, idx) => {
				const value = coerceNumber(def.getValue(row));
				const weekRaw = row.year_week || "";
				return {
					value,
					weekRaw,
					shortWeek: shortWeekLabel(weekRaw, idx),
					fullWeek: fullWeekLabel(weekRaw, idx),
					periodLabel: formatPeriod(row.period),
				};
			});
			const points = data.filter((point) => point.value != null);
			const latestPoint = points.length ? points[points.length - 1] : null;
			const previousPoint = points.length > 1 ? points[points.length - 2] : null;
			return {
				...def,
				color: def.color || METRIC_PALETTE[index % METRIC_PALETTE.length],
				data,
				hasData: points.length > 0,
				latestPoint,
				previousPoint,
			};
		});
	}, [filteredHistory]);

	useEffect(() => {
		if (!groupedHistoryCharts.length) return;
		const current = groupedHistoryCharts.find(
			(chart) => chart.key === selectedMetricKey
		);
		if (!current || !current.hasData) {
			const fallback =
				groupedHistoryCharts.find((chart) => chart.hasData) ||
				groupedHistoryCharts[0];
			if (fallback) setSelectedMetricKey(fallback.key);
		}
	}, [groupedHistoryCharts, selectedMetricKey]);

	const selectedMetric =
		groupedHistoryCharts.find((chart) => chart.key === selectedMetricKey) ||
		groupedHistoryCharts.find((chart) => chart.hasData) ||
		groupedHistoryCharts[0] ||
		null;

	if (!dealerId) {
		return (
			<Typography variant="body2">
				Select a dealer to view insights.
			</Typography>
		);
}

	if (loading && !selectedWeekEntry) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minHeight: 320,
				}}
			>
				<CircularProgress size={28} />
			</Box>
		);
	}

	if (error && !selectedWeekEntry) {
		return (
			<Box sx={{ p: 2 }}>
				<Alert severity="error">{error}</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<Card>
				<CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", md: "row" },
							gap: 2,
							alignItems: { md: "center" },
							justifyContent: "space-between",
						}}
					>
						<Box>
							<Typography variant="h6">Weekly Insights</Typography>
							<Typography variant="body2" color="text.secondary">
								{dealerName ? `${dealerName} • ` : ""}
								{selectedWeekEntry?.year_week
									? `Week ${selectedWeekEntry.year_week}`
									: "Latest"}
								{selectedWeekEntry?.period
									? ` • ${formatPeriod(selectedWeekEntry.period)}`
									: ""}
							</Typography>
							{selectedWeekEntry?.computed_at && (
								<Typography variant="caption" color="text.secondary">
									Computed {dayjs(selectedWeekEntry.computed_at).format(
										"DD MMM YYYY, HH:mm"
									)}
								</Typography>
							)}
						</Box>
						{weekOptions.length > 1 && (
							<TextField
								select
								label="Week"
								size="small"
								value={selectedWeek ?? ""}
								onChange={(event) => setSelectedWeek(event.target.value)}
								sx={{ minWidth: 220 }}
							>
								{weekOptions.map((option) => (
									<MenuItem key={option.id} value={option.id}>
										<Box
											sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
										>
											<Typography variant="body2" fontWeight={600}>
												{option.label}
											</Typography>
											{option.period && (
												<Typography variant="caption" color="text.secondary">
													{option.period}
												</Typography>
											)}
										</Box>
									</MenuItem>
								))}
							</TextField>
						)}
					</Box>
					{selectedWeekEntry ? (
						<Grid container spacing={2}>
							{SINGLE_SECTIONS.map((section) => (
								<Grid item key={section.key} xs={12} md={section.span}>
									<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
										<Typography
											variant="subtitle2"
											color="text.secondary"
											gutterBottom
										>
											{section.title}
										</Typography>
										<Box sx={{ display: "grid", rowGap: 1.25 }}>
											{section.metrics.map((metric) => (
												<Box
													key={metric.label}
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														gap: 2,
													}}
												>
													<Typography
														variant="body2"
														color="text.secondary"
													>
														{metric.label}
													</Typography>
													<Typography variant="body2" fontWeight={600}>
														{metric.formatter(
															metric.getValue(selectedWeekEntry)
														)}
													</Typography>
												</Box>
											))}
										</Box>
									</Paper>
								</Grid>
							))}
						</Grid>
					) : (
						<Box sx={{ py: 6 }}>
							<Typography variant="body2" color="text.secondary">
								No weekly insights available yet.
							</Typography>
						</Box>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardContent>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", md: "row" },
							alignItems: { md: "center" },
							justifyContent: "space-between",
							gap: 2,
						}}
					>
						<Box>
							<Typography variant="h6">Historical Insights</Typography>
							<Typography variant="body2" color="text.secondary">
								{history.length > 0
									? `Showing ${
											selectedRange.count === Infinity
												? history.length
												: Math.min(history.length, selectedRange.count)
									  } of ${history.length} weeks`
									: "No historical data"}
							</Typography>
						</Box>
						<ToggleButtonGroup
							value={rangeKey}
							exclusive
							size="small"
							onChange={(_, value) => value && setRangeKey(value)}
							sx={{
								gap: 1,
								flexWrap: "wrap",
								"& .MuiToggleButton-root": { textTransform: "none" },
							}}
						>
							{HISTORY_RANGE_OPTIONS.map((option) => (
								<ToggleButton key={option.key} value={option.key}>
									{option.label}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Box>
				</CardContent>
				<Divider />
				{error && history.length > 0 ? (
					<Box sx={{ p: 2 }}>
						<Alert severity="warning">{error}</Alert>
					</Box>
				) : !selectedMetric || !selectedMetric.hasData ? (
					<Box sx={{ p: 3 }}>
						<Typography variant="body2" color="text.secondary">
							{history.length > 0
								? "No datapoints for the selected metric in this range."
								: "Historical data will appear once available."}
						</Typography>
					</Box>
				) : (
					<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Metric
							</Typography>
							{isSmallScreen ? (
								<TextField
									select
									label="Metric"
									size="small"
									value={selectedMetric?.key ?? ""}
									onChange={(event) =>
										setSelectedMetricKey(event.target.value)
									}
									SelectProps={{
										MenuProps: {
											PaperProps: { sx: { maxHeight: 360 } },
										},
									}}
									sx={{ minWidth: 220 }}
								>
									<ListSubheader disableSticky>Metrics</ListSubheader>
									{groupedHistoryCharts.map((chart) => (
										<MenuItem
											key={chart.key}
											value={chart.key}
											sx={{ opacity: chart.hasData ? 1 : 0.6 }}
										>
											{chart.label}
											{chart.hasData ? "" : " (no data)"}
										</MenuItem>
									))}
								</TextField>
							) : (
								<ToggleButtonGroup
									value={selectedMetric?.key ?? ""}
									exclusive
									onChange={(_, value) => value && setSelectedMetricKey(value)}
									size="small"
									sx={{
										flexWrap: "wrap",
										gap: 1,
										"& .MuiToggleButton-root": {
											textTransform: "none",
											minWidth: 140,
										},
									}}
								>
									{groupedHistoryCharts.map((chart) => (
										<ToggleButton
											key={chart.key}
											value={chart.key}
											sx={{ opacity: chart.hasData ? 1 : 0.6 }}
										>
											{chart.label}
										</ToggleButton>
									))}
								</ToggleButtonGroup>
							)}
						</Box>

						{selectedMetric && (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
									<Typography variant="h5" fontWeight={600}>
										{selectedMetric.latestPoint
											? formatHistoryValue(
													selectedMetric.latestPoint.value,
													selectedMetric
											  )
											: "—"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{selectedMetric.latestPoint?.fullWeek || "Latest"}
										{selectedMetric.latestPoint?.periodLabel &&
										selectedMetric.latestPoint.periodLabel !== "—"
											? ` • ${selectedMetric.latestPoint.periodLabel}`
											: ""}
									</Typography>
									{selectedMetric.previousPoint?.value != null &&
										selectedMetric.latestPoint?.value != null && (
											<Typography
												variant="body2"
												sx={{
													color:
														selectedMetric.latestPoint.value -
															selectedMetric.previousPoint.value >
														0
															? theme.palette.success.main
															: selectedMetric.latestPoint.value -
																	selectedMetric.previousPoint.value <
															  0
															? theme.palette.error.main
															: theme.palette.text.secondary,
												}}
											>
												{selectedMetric.latestPoint.value -
													selectedMetric.previousPoint.value >
												0
													? "▲ "
													: selectedMetric.latestPoint.value -
															selectedMetric.previousPoint.value <
													  0
													? "▼ "
													: ""}
												{formatHistoryDelta(
													selectedMetric.latestPoint.value,
													selectedMetric.previousPoint.value,
													selectedMetric
												)}
											</Typography>
										)}
								</Box>

								<Box sx={{ width: "100%", height: { xs: 260, sm: 320 } }}>
									<ResponsiveContainer width="100%" height="100%">
										<LineChart
											data={selectedMetric.data}
											margin={{ top: 16, right: 20, left: 0, bottom: 16 }}
										>
											<CartesianGrid
												strokeDasharray="4 3"
												stroke={theme.palette.action.hover}
											/>
											<XAxis
												dataKey="shortWeek"
												tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
												interval="preserveStartEnd"
												minTickGap={12}
												axisLine={{ stroke: theme.palette.divider }}
												tickLine={false}
											/>
											<YAxis
												tickFormatter={(value) =>
													formatHistoryTick(value, selectedMetric)
												}
												width={
													selectedMetric.type === "percent" ? 76 : 68
												}
												axisLine={{ stroke: theme.palette.divider }}
												tickLine={false}
												tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
											/>
											<Tooltip
												formatter={(value) => [
													formatHistoryValue(value, selectedMetric),
													selectedMetric.label,
												]}
												labelFormatter={(label, payload) => {
													if (!payload || !payload.length) return label;
													const dataPoint = payload[0].payload;
													const weekLabel =
														dataPoint.fullWeek || label || "Week";
													const period = dataPoint.periodLabel;
													return period && period !== "—"
														? `${weekLabel} • ${period}`
														: weekLabel;
												}}
												contentStyle={{
													borderRadius: 8,
													borderColor: theme.palette.divider,
													boxShadow: theme.shadows[3],
												}}
											/>
											<Line
												dataKey="value"
												type="monotone"
												stroke={
													selectedMetric.color ||
													theme.palette.primary.main
												}
												strokeWidth={2.6}
												dot={{ r: 0 }}
												activeDot={{
													r: 5,
													fill:
														selectedMetric.color ||
														theme.palette.primary.main,
												}}
											/>
										</LineChart>
									</ResponsiveContainer>
								</Box>
							</Box>
						)}
					</Box>
				)}
			</Card>
		</Box>
	);
}
